import BasicCard from "@components/ArticleCard/BasicCard";
import { CardVariations } from "@components/ArticleCard/types";
import { useWindowSize } from "@hooks/useWindowSize";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { SanitizedSectionArticle } from "@typings/Cue";
import { cn, getFormattedUrlPath } from "@util/helpers";

type HighlightsSubLeadProps = {
  /** The classname of the component. */
  className?: string;
  /** The article to be displayed as the lead highlight. */
  articles: SanitizedSectionArticle[];
};

/** This is the next 6 articles after the lead in the highlights component.
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79061&m=dev
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79072&m=dev
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79083&m=dev
 */
const HighlightsSubLead = ({
  className,
  articles,
}: HighlightsSubLeadProps): React.ReactElement => {
  const { isScreenMD, isScreenXL } = useWindowSize();
  const OktaUserInfo = useOKTAUserStore((store) => store.userInfo);
  const isSubscriber = OktaUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;

  const variant: CardVariations = {
    image: {
      position: "left",
      width: cn("w-120px"),
    },
    content: {
      width: cn("w-full-120px"),
      extraClass: cn("md:pl-4"),
    },
    title: {
      color: cn("text-gray-850"),
      size: cn("text-base"),
      extraClass: cn("font-semibold leading-[1.25rem]"),
    },
    kicker: {
      color: cn("text-gray-515"),
    },
  };
  const group = () => {
    if (isScreenXL) return 2;

    return 3;
  };

  const groupedArticles = new Array(4)
    // Make sure each element isn't empty
    .fill("")
    // For each group, grab the right `slice` of the input array
    .map((_, i) => articles.slice(i * group(), (i + 1) * group()));

  return (
    <div
      className={cn("grid gap-8", className)}
      data-testid="highlights-sub-lead-component"
    >
      {groupedArticles.map((_articles, index) => {
        return (
          <div
            key={index}
            className={cn(
              "relative grid gap-8",
              "lg:grid-cols-3",
              "lg:after:absolute lg:after:-bottom-4 lg:after:h-[1px] lg:after:w-full lg:after:bg-gray-175",
              "xl:grid-cols-2"
            )}
          >
            {_articles.map(
              (
                { id, title, urlPath, media, kicker, sections, paywall },
                _index
              ) => {
                const defaultImage = {
                  directoryName: sections?.[0]?.directoryName || "",
                };
                const link = getFormattedUrlPath(urlPath, [
                  { key: "ref", value: "btsg60-highlights" },
                ]);

                return (
                  <BasicCard
                    key={id}
                    id={id}
                    title={title}
                    slug={link}
                    media={isScreenMD ? media : undefined}
                    kicker={kicker?.fields?.[0]?.value}
                    variations={variant}
                    defaultImage={isScreenMD ? defaultImage : undefined}
                    paywall={
                      isSubscriber ? paywall.contentAccess === "1" : undefined
                    }
                    rootClassName={cn(
                      "relative after:absolute after:-bottom-4 after:h-[1px] after:w-full after:bg-gray-175 lg:after:hidden",
                      {
                        "lg:before:absolute lg:before:-left-4 lg:before:h-full lg:before:w-[1px] lg:before:bg-gray-175":
                          _index >= 1,
                      }
                    )}
                  />
                );
              }
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HighlightsSubLead;
